// File: src/components/landing/OurStorySection.jsx
import React, { useState } from "react";
import { IoChevronDown } from "react-icons/io5";

export default function OurStorySection() {
  const [expanded, setExpanded] = useState(false);
  return (
    <section id="our-story" className="tribeStory" aria-labelledby="tribe-story-title">
      <style>{`
        .tribeStory {
          background: #fff;
          color: #171717;
          border-top: 1px solid rgba(17,17,17,0.08);
          font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        .tribeStory, .tribeStory * { box-sizing: border-box; }
        .tribeStory__inner {
          max-width: 1180px;
          margin: 0 auto;
          padding: 90px 28px 96px;
          display: grid;
          grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
          gap: 60px;
          align-items: start;
        }
        .tribeStory__eyebrow {
          margin: 0;
          font-family: 'Ugly Handwriting', cursive;
          font-size: 1.25rem;
          font-weight: 500;
          line-height: 1.15;
          color: #343c54;
        }
        .tribeStory__title {
          max-width: 460px;
          margin: 12px 0 0;
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: clamp(2rem, 3.7vw, 2.8rem);
          font-style: normal;
          font-weight: 400;
          line-height: 1.08;
          letter-spacing: -0.015em;
          color: #171717;
        }
        .tribeStory__copy {
          max-width: 560px;
          padding-top: 4px;
        }
        .tribeStory__copy p {
          margin: 0 0 18px;
          font-size: 0.98rem;
          font-weight: 400;
          line-height: 1.7;
          letter-spacing: -0.01em;
          color: #555;
        }
        .tribeStory__copy p:last-child { margin-bottom: 0; }
        .tribeStory__more { display: grid; grid-template-rows: 1fr; }
        .tribeStory__moreInner { min-height: 0; overflow: hidden; }
        .tribeStory__moreContent { padding-top: 20px; }
        .tribeStory__copy .tribeStory__intro { margin-bottom: 0; }
        .tribeStory__photos {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
          margin: 24px 0;
        }
        .tribeStory__photo {
          display: block;
          width: 100%;
          aspect-ratio: 4 / 3;
          object-fit: cover;
          object-position: center;
          background: #eeeae3;
        }
        .tribeStory__mission {
          margin: 24px 0 18px;
          padding-top: 20px;
          border-top: 1px solid rgba(17,17,17,0.12);
        }
        .tribeStory__missionLabel {
          display: block;
          margin-bottom: 8px;
          color: #777;
          font-size: 11px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }
        .tribeStory__copy .tribeStory__missionText {
          margin: 0;
          color: #171717;
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: clamp(1.65rem, 2.5vw, 2rem);
          line-height: 1.15;
          letter-spacing: -0.015em;
        }
        .tribeStory__toggle { display: none; }
        @media (max-width: 700px) {
          .tribeStory__inner {
            grid-template-columns: minmax(0, 1fr);
            gap: 30px;
          }
        }
        @media (max-width: 450px) {
          .tribeStory__inner { padding: 70px 22px 76px; }
          .tribeStory__title {
            margin-top: 10px;
            font-size: clamp(2.05rem, 8.6vw, 2.55rem);
          }
          .tribeStory__copy { padding-top: 0; }
          .tribeStory__copy p { font-size: 0.94rem; line-height: 1.65; }
          .tribeStory__more {
            grid-template-rows: 0fr;
            opacity: 0;
            visibility: hidden;
            transition: grid-template-rows 300ms ease, opacity 240ms ease;
          }
          .tribeStory__more.is-expanded {
            grid-template-rows: 1fr;
            opacity: 1;
            visibility: visible;
          }
          .tribeStory__toggle {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            margin-top: 24px;
            padding: 0 0 3px;
            border: 0;
            border-bottom: 1px solid rgba(0,0,0,0.28);
            background: transparent;
            color: #171717;
            font-family: inherit;
            font-size: 0.96rem;
            font-weight: 680;
            letter-spacing: -0.015em;
            text-transform: lowercase;
            -webkit-tap-highlight-color: transparent;
            cursor: pointer;
          }
          .tribeStory__toggle:focus-visible {
            outline: 2px solid #171717;
            outline-offset: 4px;
          }
          .tribeStory__toggle svg { transition: transform 240ms ease; }
          .tribeStory__toggle[aria-expanded="true"] svg { transform: rotate(180deg); }
        }
        @media (prefers-reduced-motion: reduce) {
          .tribeStory__more, .tribeStory__toggle svg { transition: none; }
        }
      `}</style>

      <div className="tribeStory__inner">
        <header>
          <p className="tribeStory__eyebrow">our story</p>
          <h2 id="tribe-story-title" className="tribeStory__title">
            a city full of people.
            <br />
            a place to belong.
          </h2>
        </header>
        <div className="tribeStory__copy">
          <p className="tribeStory__intro">
            You can live in a city full of people and still feel like you haven’t
            found yours. Tribe is here to make that first connection easier.
          </p>
          <div
            id="tribe-story-more"
            className={`tribeStory__more${expanded ? " is-expanded" : ""}`}
          >
            <div className="tribeStory__moreInner">
              <div className="tribeStory__moreContent">
                <p>
                  We believe meeting people should feel like a natural part of
                  life—something you look forward to, with room to be yourself.
                </p>
                <p>
                  Our gatherings give people something to share, from dinner
                  to an afternoon exploring somewhere new. Our hosts help everyone
                  settle in, so showing up alone feels a little easier.
                </p>
                <div className="tribeStory__photos" aria-hidden="true">
                  {[
                    "https://i.pinimg.com/736x/8d/00/01/8d0001e14312a07bcf1080f77a6ae995.jpg",
                    "https://i.pinimg.com/736x/a4/03/b5/a403b5aa7ab025f6d06fa42ca8c5cac1.jpg",
                    "https://i.pinimg.com/736x/fc/65/86/fc65863ea39d138606fa0d900dd31d3a.jpg",
                  ].map((src) => (
                    <img
                      key={src}
                      className="tribeStory__photo"
                      src={src}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      width={400}
                      height={300}
                    />
                  ))}
                </div>
                <div className="tribeStory__mission">
                  <span className="tribeStory__missionLabel">our mission</span>
                  <p className="tribeStory__missionText">Make loneliness an option.</p>
                </div>
                <p>
                  More chances for strangers to become familiar faces, and for
                  those familiar faces to become part of your life.
                </p>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="tribeStory__toggle"
            aria-expanded={expanded}
            aria-controls="tribe-story-more"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? "Read less" : "Read more"}
            <IoChevronDown size={15} aria-hidden="true" />
          </button>
        </div>
      </div>
    </section>
  );
}
